import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { COLORS } from "../../common/colors";
import { SWATCH } from "./complianceColors";
import {
  AXIS_GUTTER,
  DAY_CELL_GAP,
  DAY_CELL_WIDTH,
  DayAxis,
  formatDay
} from "./dayTimeline";

interface BarSegment {
  value: number;
  color: string;
  stroke?: string;
  // Diagonal lines in this colour, so segments differ by more than hue.
  hatch?: string;
}

export function integerTicks(yMax: number): number[] {
  const ticks: number[] = [];
  const step = yMax <= 4 ? 1 : Math.ceil(yMax / 4);
  for (let i = 0; i <= yMax; i += step) ticks.push(i);
  if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);
  return ticks;
}

export function ChartSwatch({
  color,
  stroke,
  hatch
}: {
  color: string;
  stroke?: string;
  hatch?: string;
}): JSX.Element {
  return (
    <Box
      sx={{
        width: 12,
        height: 12,
        flexShrink: 0,
        borderRadius: "3px",
        backgroundColor: color,
        ...(hatch && { backgroundImage: hatchImage(hatch) }),
        ...(stroke && {
          boxShadow: `inset 0 0 0 ${BAR_STROKE_WIDTH} ${stroke}`
        })
      }}
    />
  );
}

export function hatchImage(color: string): string {
  return `repeating-linear-gradient(45deg, ${color} 0 1.5px, transparent 1.5px 5px)`;
}

interface DayBar {
  date: string;
  segments: BarSegment[];
  tooltip: React.ReactNode;
  // Draw a minimum-height bar even when the value is 0, eg MM4PD data that rounds to 0h.
  showAtZero?: boolean;
}

interface DailyBarChartProps {
  dates: string[];
  bars: DayBar[];
  yMax: number;
  yTicks: number[];
  formatTick: (tick: number) => string;
  height: number;
  threshold?: { value: number; label: string };
  todayDate?: string;
  yLabel?: string;
}

const SMALL_TEXT_SX = {
  fontFamily: "Work Sans, sans-serif",
  color: COLORS.GREY_900
};

const AXIS_COLOR = COLORS.GREY_800;
const MIN_SEGMENT_HEIGHT = 4;
// Thinner than the traffic lights' 2px: bars sit side by side, so heavier outlines crowd.
const BAR_STROKE_WIDTH = "1px";

function valueToY(value: number, yMax: number, height: number): number {
  return (Math.min(value, yMax) / yMax) * height;
}

export function DailyBarChart({
  dates,
  bars,
  yMax,
  yTicks,
  formatTick,
  height,
  threshold,
  todayDate,
  yLabel
}: DailyBarChartProps): JSX.Element {
  const barsByDate = new Map(bars.map((b) => [b.date, b]));

  return (
    <Box>
      {yLabel && (
        <Typography sx={{ ...SMALL_TEXT_SX, fontSize: 11, mb: 0.75 }}>
          {yLabel}
        </Typography>
      )}
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            position: "relative",
            width: AXIS_GUTTER,
            height,
            flexShrink: 0,
            boxSizing: "border-box",
            borderRight: `1px solid ${AXIS_COLOR}`
          }}
        >
          {yTicks.map((t) => (
            <Typography
              key={t}
              sx={{
                ...SMALL_TEXT_SX,
                position: "absolute",
                right: 4,
                bottom: valueToY(t, yMax, height),
                transform: "translateY(50%)",
                fontSize: 11,
                lineHeight: 1
              }}
            >
              {formatTick(t)}
            </Typography>
          ))}
        </Box>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            gap: `${DAY_CELL_GAP}px`,
            alignItems: "flex-end",
            height
          }}
        >
          {yTicks.map((t) => (
            <Box
              key={t}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: valueToY(t, yMax, height),
                // The zero line is the x-axis: dark, so a day with no bar reads as missing data.
                borderTop: `1px solid ${
                  t === 0 ? AXIS_COLOR : COLORS.GREY_300
                }`,
                pointerEvents: "none"
              }}
            />
          ))}
          {dates.map((date) => {
            const bar = barsByDate.get(date);
            const isToday = todayDate && date === todayDate;
            const segments = bar?.segments ?? [];
            const nonZero = segments.filter((seg) => seg.value > 0);
            const visible =
              nonZero.length === 0 && bar?.showAtZero
                ? segments.slice(0, 1)
                : nonZero;
            // Stacked by drawn height, so a part raised to the minimum doesn't overlap the next.
            const drawnHeights = visible.map((seg) =>
              Math.max(MIN_SEGMENT_HEIGHT, valueToY(seg.value, yMax, height))
            );
            const stackHeight = drawnHeights.reduce((sum, h) => sum + h, 0);

            let accumulatedHeight = 0;
            const segmentBoxes = visible.map((seg, idx) => {
              const segHeight = drawnHeights[idx];
              const segBottom = accumulatedHeight;
              accumulatedHeight += segHeight;
              const isTopSegment = idx === visible.length - 1;

              return (
                <Box
                  key={idx}
                  sx={{
                    position: "absolute",
                    bottom: segBottom,
                    left: 0,
                    width: DAY_CELL_WIDTH,
                    height: segHeight,
                    backgroundColor: seg.color,
                    ...(seg.hatch && {
                      backgroundImage: hatchImage(seg.hatch)
                    }),
                    ...(seg.stroke && {
                      boxShadow: `inset 0 0 0 ${BAR_STROKE_WIDTH} ${seg.stroke}`
                    }),
                    borderRadius: isTopSegment ? "4px 4px 0 0" : 0
                  }}
                />
              );
            });

            return (
              <Tooltip
                key={date}
                title={bar?.tooltip ?? `${formatDay(date)}: no data`}
                // The hover area is the full column, so anchor to the cursor rather than its top.
                followCursor
                arrow
                placement="top"
              >
                {/* Full height, so days with no bar still have something to hover. */}
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0,
                    width: DAY_CELL_WIDTH,
                    height,
                    // Marks today whether or not anything has arrived yet.
                    ...(isToday && {
                      outline: `1px dashed ${SWATCH.NEUTRAL_STROKE}`,
                      outlineOffset: "1px",
                      borderRadius: "4px"
                    })
                  }}
                >
                  {visible.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: DAY_CELL_WIDTH,
                        height: stackHeight
                      }}
                    >
                      {segmentBoxes}
                    </Box>
                  )}
                </Box>
              </Tooltip>
            );
          })}
          {threshold && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: valueToY(threshold.value, yMax, height),
                borderTop: `2px dashed ${COLORS.GREY_600}`,
                pointerEvents: "none",
                zIndex: 2
              }}
            >
              <Typography
                sx={{
                  ...SMALL_TEXT_SX,
                  position: "absolute",
                  right: -24,
                  top: -8,
                  fontSize: 11
                }}
              >
                {threshold.label}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <DayAxis dates={dates} />
    </Box>
  );
}
